import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { paymentsShape } from 'constants/propTypes';
import { COVID_VOUCHER_REFUND, providers } from 'constants/payment';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Button from 'components/ui/Button';
import { CurrencyInput } from 'components/ui/Inputs';
import CardSelect from 'components/ui/CardSelect';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ErrorMessage, FormWrapper, Spacer, ProviderLabel, AmountLabel } from '../CardRefund/CardRefund.styles';
import PaymentAgeWarning from '../PaymentAgeWarning';

const SofortRefund = ({
  currency,
  totalPriceToBePaid,
  totalAvailableRefundAmount,
  payments,
  reference,
  bookingReference,
  isCancelVoucher,
  mainBookingRef,
  processCardPaymentRefund,
  paymentLocation,
  hasHotel
}) => {
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);

  const defaultRefundAmount = Number(Math.min(totalPriceToBePaid, totalAvailableRefundAmount).toFixed(2));

  const { register, handleSubmit, clearErrors, formState, watch } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;
  const selectedReference = watch('reference');

  const [isDisabledRefund, setDisabledRefund] = useState(false);
  const [maxAmount, setMaxAmount] = useState(defaultRefundAmount);
  const [selectedAmount, setSelectedAmount] = useState(defaultRefundAmount);

  // eslint-disable-next-line consistent-return
  const onRefundSofort = async ({ amount, reference: pspReference }) => {
    try {
      await processCardPaymentRefund({
        variables: {
          bookingReference,
          refundAmount: parseFloat(amount),
          pspReference,
          currency,
          paymentLocation,
          hasHotel
        }
      });

      if (isCancelVoucher) {
        addNote({
          variables: {
            note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
            bookingReference: mainBookingRef
          }
        });

        addNote({
          variables: {
            note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
            bookingReference
          }
        });
      }
    } catch (err) {
      return err;
    }
  };

  const handleOnSubmit = ({ reference, amount }) => onRefundSofort({ reference, amount }); // eslint-disable-line no-shadow
  const hasSofortError = Boolean(errors.reference);
  const hasAmountError = Boolean(errors.amount);

  if (totalAvailableRefundAmount === 0) {
    return (
      <p data-cy="noRefundLeft">
        <FormattedMessage id="refunds.card.noRefundLeftOnMethod" />
      </p>
    );
  }

  return (
    <form data-testid="sofort-refund-form" data-cy="refund-form" onSubmit={handleSubmit(handleOnSubmit)}>
      <FormWrapper>
        <header>
          <h2 data-cy="title">
            <FormattedMessage id="refunds.sofort.title" />
          </h2>
        </header>

        <Spacer>
          <CurrencyInput
            currency={currency}
            dataCy="amount-input"
            dataTestId="amount-input"
            defaultValue={defaultRefundAmount}
            isControlled={false}
            {...register('amount', {
              required: true,
              min: 1,
              max: defaultRefundAmount,
              onChange: (e) => {
                setSelectedAmount(e.target.value);
                setDisabledRefund(e.target.value > maxAmount || e.target.value > defaultRefundAmount);
              }
            })}
            step={0.01}
          />
        </Spacer>

        <h3>
          <FormattedMessage id="refunds.sofort.selectPayment" />
        </h3>
        {payments.map((payment) => (
          <CardSelect
            key={payment.refId}
            id={payment.refId}
            testId={payment.refId}
            value={payment.reference}
            onClick={() => {
              setMaxAmount(payment.amount);
              setDisabledRefund(selectedAmount > payment.amount);
            }}
            {...register('reference', { required: true, onChange: () => clearErrors('reference') })}
            disabled={selectedAmount > payment.amount || currency !== payment.currency}
          >
            <div>
              <span>{payment.reference}</span>
              <ProviderLabel data-test={`card-${reference}-provider`}>
                {payment.provider === providers.NONE && 'datacash'}
                {payment.provider === providers.ADYEN && 'adyen'}
              </ProviderLabel>
            </div>
            <div>
              <AmountLabel>
                <FormattedMessage id="refunds.original" />
              </AmountLabel>
              <FormattedCurrency
                dataCy={`original-amount-${payment.reference}`}
                dataTestId={`original-amount-${payment.reference}`}
                currency={currency}
                amount={payment?.amountInOriginalCurrency || payment?.amount || 0}
              />
            </div>
            <div>
              <AmountLabel>
                <FormattedMessage id="refunds.available" />
              </AmountLabel>
              <FormattedCurrency
                dataCy={`available-amount-${payment.reference}`}
                dataTestId={`available-amount-${payment.reference}`}
                currency={currency}
                amount={payment?.availableRefundAmount || 0}
              />
            </div>
          </CardSelect>
        ))}

        {hasAmountError && (
          <ErrorMessage data-cy="amount-error-msg">
            <FormattedMessage
              id="refunds.card.error.amount"
              values={{
                amount: <FormattedCurrency currency={currency} amount={defaultRefundAmount} />
              }}
            />
          </ErrorMessage>
        )}
        {hasSofortError && (
          <ErrorMessage data-cy="sofort-error-msg">
            <FormattedMessage id="refunds.sofort.selectPayment" />
          </ErrorMessage>
        )}
        {selectedAmount > maxAmount && (
          <ErrorMessage data-cy="sofortAmount-error-msg">
            <FormattedMessage id="refunds.sofort.error.maxamount" />
          </ErrorMessage>
        )}
        <PaymentAgeWarning
          payment={payments.find((payment) => payment.reference === selectedReference)}
          amount={selectedAmount}
          minAmount={1}
          maxAmount={Math.min(maxAmount, defaultRefundAmount)}
          hasError={hasAmountError || hasSofortError || isDisabledRefund}
        />
        <Button data-testid="submit" data-cy="submit" type="submit" disabled={isDisabledRefund} variant="primary">
          <FormattedMessage id="refunds.card.submit" />
        </Button>
      </FormWrapper>
    </form>
  );
};

SofortRefund.propTypes = {
  currency: PropTypes.string,
  payments: paymentsShape,
  totalPriceToBePaid: PropTypes.number,
  totalAvailableRefundAmount: PropTypes.number,
  bookingReference: PropTypes.string,
  reference: PropTypes.string,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  processCardPaymentRefund: PropTypes.func.isRequired,
  paymentLocation: PropTypes.string.isRequired,
  hasHotel: PropTypes.bool
};
export default SofortRefund;
